import React, { useState } from 'react';
import { Box, Button, TextField, Typography } from '@mui/material';
import Rating from '@mui/material/Rating';

interface RatingCommentFormProps {
    /**
     * Callback when user submits a rating and comment
     * @param data { rating: number; comment: string }
     */
    onSubmit: (data: { rating: number; comment: string }) => void;
    /** Optional initial rating value */
    initialRating?: number;
    /** Optional initial comment */
    initialComment?: string;
    /** Hide the star section of this comment */
    isReply: boolean;
}

const RatingCommentForm: React.FC<RatingCommentFormProps> = ({ onSubmit, initialRating = 0, initialComment = '', isReply = false }) => {
    const [rating, setRating] = useState<number | null>(initialRating);
    const [comment, setComment] = useState<string>(initialComment);
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = async () => {
        if (rating === null || comment.trim() === '') return;
        setSubmitting(true);
        try {
            await Promise.resolve(onSubmit({ rating, comment }));
            // Reset form or leave as is
        } catch (err) {
            console.error(err);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                p: 2,
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                backgroundColor: '#fafafa',
            }}
        >
            {!isReply &&
                <Typography variant="h6">Rate this restaurant</Typography>
            }
            {
                !isReply &&
                <Rating
                    name="restaurant-rating"
                    value={rating}
                    precision={1}
                    onChange={(_, value) => setRating(value)}
                />
            }
            {isReply ?
                <Typography variant="h6">Reply</Typography>
                :
                <Typography variant="h6">Leave a comment</Typography>
            }
            <TextField
                multiline
                minRows={3}
                maxRows={6}
                variant="outlined"
                placeholder="Write your thoughts here..."
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                slotProps={{
                    htmlInput: {
                        maxLength: 2000,
                    },
                    formHelperText: {
                        sx: { textAlign: 'right' },
                    },
                }}
                helperText={`${comment.length}/2000 characters`}
            />

            <Button
                variant="contained"
                disabled={rating === null || comment.trim() === '' || submitting}
                onClick={handleSubmit}
                sx={{
                    backgroundColor: 'black',
                    color: 'white',
                    '&:hover': {
                        backgroundColor: '#333',
                    },
                }}
            >
                {submitting ? 'Submitting...' : 'Submit'}
            </Button>
        </Box>
    );
};

export default RatingCommentForm;
